import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown, MessageCircle, Sparkles } from "lucide-react";

interface FAQItemProps {
    question: string;
    answer: string;
    isOpen: boolean;
    onClick: () => void;
    index: number;
    isRTL: boolean;
}

interface FAQData {
    question: string;
    answer: string;
}

interface EnhancedFAQProps {
    translationNamespace: string;
    faqs?: FAQData[];
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick, index, isRTL }) => {
    return (
        <motion.div
            dir={isRTL ? "rtl" : "ltr"}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            viewport={{ once: true }}
            className="mb-3"
        >
            <div
                onClick={onClick}
                className={`
                    w-full p-4 md:p-5 rounded-2xl border transition-all duration-200 cursor-pointer flex items-center justify-between gap-4
                    ${
                        isOpen
                            ? "bg-white border-[#0072CE]/60 shadow-sm ring-1 ring-[#0072CE]/20"
                            : "bg-white/90 border-slate-200/80 hover:bg-white hover:border-slate-300"
                    }
                `}
            >
                <h3 className={`text-sm md:text-base font-bold transition-colors duration-200 ${isOpen ? "text-[#0038A8]" : "text-slate-800"}`}>
                    {question}
                </h3>

                <div
                    className={`
                        w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300
                        ${isOpen ? "bg-blue-50 text-[#0072CE] rotate-180" : "bg-slate-100 text-slate-500"}
                    `}
                >
                    <ChevronDown className="w-4 h-4" />
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="p-4 sm:p-5 pt-4 sm:pt-5 mt-1.5 text-slate-600 text-xs sm:text-sm leading-relaxed sm:leading-7 bg-slate-50/80 rounded-2xl border border-slate-200/70 shadow-2xs">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const EnhancedFAQ: React.FC<EnhancedFAQProps> = ({ translationNamespace, faqs: staticFaqs }) => {
    const { t, i18n } = useTranslation(translationNamespace);
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const ns = translationNamespace;

            try {
                const translations = await import(`./deta-FAQ/locales/${translationNamespace}.json`);

                ["fa", "en", "ar", "tr"].forEach((lng) => {
                    const langTranslations = translations.default[lng as keyof typeof translations.default];
                    if (langTranslations && !i18n.hasResourceBundle(lng, ns)) {
                        i18n.addResourceBundle(lng, ns, langTranslations, true, true);
                    }
                });
            } catch (error) {
                console.error(`Failed to load translations for ${translationNamespace}:`, error);
            }
        };

        loadTranslations();
    }, [i18n, translationNamespace]);

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    // Get FAQs from translation
    const tFaqs = t("faqs", { returnObjects: true });
    const faqs = (Array.isArray(tFaqs) && tFaqs.length > 0) ? (tFaqs as FAQData[]) : staticFaqs;

    if (!Array.isArray(faqs) || faqs.length === 0) {
        return null;
    }

    return (
        <section dir={isRTL ? "rtl" : "ltr"} className="py-12 md:py-16 relative overflow-hidden mx-auto px-4 sm:px-10">
            {/* Ambient background blur */}
            <div className="absolute top-1/2 start-0 w-80 h-80 bg-[#0072CE]/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
            <div className="absolute top-1/2 end-0 w-80 h-80 bg-[#0038A8]/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />

            <div className="container mx-auto relative z-10 max-w-6xl">
                <div className="grid lg:grid-cols-12 gap-8 items-start">
                    
                    {/* FAQ Accordion List */}
                    <div className="lg:col-span-8 space-y-2">
                        {faqs.map((item, index) => (
                            <FAQItem
                                key={index}
                                question={item.question}
                                answer={item.answer}
                                isOpen={openIndex === index}
                                onClick={() => handleToggle(index)}
                                index={index}
                                isRTL={isRTL}
                            />
                        ))}
                    </div>

                    {/* Support & Contact Card */}
                    <div className="lg:col-span-4 h-full relative">
                        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-xs flex flex-col justify-between items-center text-center h-full min-h-[400px] relative overflow-hidden">
                            
                            {/* Top CTA Pill Button */}
                            <div className="z-10 w-full flex justify-center">
                                <button
                                    onClick={() => window.open("https://wa.me/message/GNNOKQBPQVSQB1", "_blank")}
                                    className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white text-xs md:text-sm font-bold shadow-md shadow-blue-500/20 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer group"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{t("askQuestion", "سوالات خود را از ما بپرسید")}</span>
                                </button>
                            </div>

                            {/* Center Logo with Interactive Animated Rings Accent */}
                            <motion.div
                                className="relative my-8 sm:my-10 flex items-center justify-center min-h-[280px] sm:min-h-[300px] w-full cursor-pointer group"
                                whileHover="hover"
                                initial="initial"
                            >
                                {/* Outer Glow Ambient Spot */}
                                <motion.div
                                    className="absolute w-60 h-60 sm:w-72 sm:h-72 rounded-full bg-gradient-to-tr from-[#0038A8]/15 via-[#0072CE]/20 to-cyan-400/10 blur-2xl pointer-events-none"
                                    animate={{
                                        scale: [1, 1.15, 1],
                                        opacity: [0.5, 0.8, 0.5],
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                />

                                {/* Outer Ring 3 with Slow Clockwise Rotation & Orbiting Node */}
                                <motion.div
                                    className="w-72 h-72 sm:w-80 sm:h-80 rounded-full border border-dashed border-blue-200/80 flex items-center justify-center absolute"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
                                    variants={{
                                        hover: { scale: 1.05, borderColor: "rgba(0, 114, 206, 0.4)" },
                                    }}
                                >
                                    {/* Orbiting Satellite Node 1 */}
                                    <div className="absolute -top-1.5 w-3.5 h-3.5 rounded-full bg-[#0072CE] shadow-sm shadow-blue-500/50 flex items-center justify-center">
                                        <div className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                                    </div>
                                    <div className="absolute -bottom-1.5 w-2 h-2 rounded-full bg-cyan-400 opacity-60" />
                                </motion.div>

                                {/* Middle Ring 2 with Counter-Clockwise Rotation */}
                                <motion.div
                                    className="w-56 h-56 sm:w-64 sm:h-64 rounded-full border border-blue-200/90 flex items-center justify-center absolute"
                                    animate={{ rotate: -360 }}
                                    transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                                    variants={{
                                        hover: { scale: 1.06, borderColor: "rgba(0, 56, 168, 0.5)" },
                                    }}
                                >
                                    {/* Orbiting Satellite Node 2 */}
                                    <div className="absolute -start-1 w-2.5 h-2.5 rounded-full bg-[#0038A8] shadow-sm shadow-blue-600/40" />
                                    <div className="absolute -end-1 w-2 h-2 rounded-full bg-blue-300" />
                                </motion.div>

                                {/* Inner Ring 1 with Pulse Breathing Animation */}
                                <motion.div
                                    className="w-40 h-40 sm:w-48 sm:h-48 rounded-full border border-blue-300/80 flex items-center justify-center absolute bg-gradient-to-b from-blue-50/60 to-white/40 backdrop-blur-xs"
                                    animate={{
                                        scale: [1, 1.06, 1],
                                        borderColor: [
                                            "rgba(147, 197, 253, 0.6)",
                                            "rgba(0, 114, 206, 0.6)",
                                            "rgba(147, 197, 253, 0.6)",
                                        ],
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                    variants={{
                                        hover: { scale: 1.1, backgroundColor: "rgba(224, 242, 254, 0.6)" },
                                    }}
                                />

                                {/* Floating Micro-Sparkle Badges */}
                                <motion.div
                                    className="absolute -top-3 end-4 z-20 bg-white/90 backdrop-blur-md p-1.5 rounded-full shadow-sm border border-blue-100 text-[#0072CE]"
                                    animate={{ y: [0, -6, 0], rotate: [0, 10, -10, 0] }}
                                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                </motion.div>

                                <motion.div
                                    className="absolute -bottom-2 start-6 z-20 bg-white/90 backdrop-blur-md p-1.5 rounded-full shadow-sm border border-blue-100 text-[#0038A8]"
                                    animate={{ y: [0, 6, 0], rotate: [0, -10, 10, 0] }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                >
                                    <Sparkles className="w-3 h-3" />
                                </motion.div>

                                {/* Central Logo Emblem (Enlarged & Highly Interactive) */}
                                <motion.div
                                    className="relative z-10 p-3 sm:p-4 rounded-[2.5rem] flex items-center justify-center"
                                    animate={{
                                        y: [0, -6, 0],
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                    whileHover={{
                                        scale: 1.15,
                                        rotate: [0, -3, 3, 0],
                                        transition: { duration: 0.35, ease: "easeOut" },
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <img
                                        src="/media/Logo1.svg"
                                        alt="لوگوی دلبند"
                                        className="h-24 sm:h-28 md:h-32 w-auto object-contain max-w-[240px] sm:max-w-[270px] drop-shadow-md transition-transform duration-300 filter group-hover:drop-shadow-xl"
                                    />
                                </motion.div>
                            </motion.div>

                            {/* Bottom Tagline */}
                            <div className="z-10 pt-2">
                                <p className="text-slate-700 text-xs md:text-sm font-bold">
                                    {t("tagline", "دلبند، همراه شما در مراقبت")}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default EnhancedFAQ;
